<script setup>
/* global axios */
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import {
  ref,
  computed,
  markRaw,
  onMounted,
  watch,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import {
  useRoute,
  useRouter,
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
} from 'vue-router';
import '@vue-flow/core/dist/style.css';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import FlowNode from './FlowNode.vue';
import FlowCanvas from './FlowCanvas.vue';
import IdeaMaps from './IdeaMaps.vue';
import {
  types,
  tokens,
  eventLabels,
  conditionLabels,
  uid,
  node,
  edge,
  newFlow,
  title,
  canConnect,
  serialize,
  preview,
  errorText,
  cloneSettings,
} from './model';

const route = useRoute();
const router = useRouter();
const accountId = computed(() => route.params.accountId);
const groups = ref([]);
const sources = ref([]);
const groupId = ref('');
const section = ref('flows');
const ideaEditor = ref(null);
const doc = ref(null);
const revision = ref(0);
const flowId = ref('');
const selectedId = ref('');
const loading = ref(true);
const saving = ref(false);
const denied = ref(false);
const error = ref('');
const notice = ref('');
const dirty = ref(false);
const uncertain = ref(false);
const listMode = ref(false);
const history = ref([]);
const executions = ref([]);
const purchases = ref([]);
const historyNext = ref(null);
const purchaseNext = ref(null);
const newType = ref('message');
const selectedEdge = ref('');
const tabs = [
  { label: 'Тарифы', key: 'rates' },
  { label: 'Сценарии', key: 'flows' },
  { label: 'История', key: 'history' },
  { label: 'Карта идей', key: 'ideas' },
];
const nodeTypes = Object.fromEntries(
  types.map(t => [t.value, markRaw(FlowNode)])
);
const flow = computed(() => doc.value?.flows.find(f => f.id === flowId.value));
const selected = computed(() =>
  flow.value?.nodes.find(n => n.id === selectedId.value)
);
const copyDialog = ref(null);
const copyGroup = ref('');
const rateDialog = ref(null);
const rateEdit = ref(null);
const buyDialog = ref(null);
const buy = ref(null);
const buyKey = ref(null);
const buyPending = ref(null);
const lastTick = ref(null);
const buyBusy = ref(false);
const buyUncertain = ref(false);
const offers = ref([]);
const previewDialog = ref(null);
const simulateDialog = ref(null);
const simulation = ref([]);
const simNode = ref('');
const simulationDone = ref(false);
const previewBody = computed(() =>
  selected.value?.type === 'message'
    ? preview(
        selected.value.data.text,
        doc.value,
        flow.value,
        groups.value.find(g => String(g.id) === groupId.value)?.title
      )
    : 'Выберите блок сообщения.'
);
let controller = new AbortController();
let generation = 0;
const canvas = ref(null);
const canvasKey = ref(0);
watch(
  flow,
  () => {
    canvasKey.value += 1;
  },
  { flush: 'sync' }
);
const fitView = options => canvas.value?.fitView(options);
const zoomIn = () => canvas.value?.zoomIn();
const zoomOut = () => canvas.value?.zoomOut();
const statusLabels = {
  queued: 'Ожидает отправки',
  handed_off: 'Передано на отправку',
  sent: 'Отправлено',
  delivered: 'Доставлено',
  read: 'Прочитано',
  cancelled: 'Отменено',
  failed: 'Ошибка отправки',
  unknown: 'Исход неизвестен — нужна проверка',
};
const selectClass =
  'rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 max-w-full';
const edits = ref([]);
const redoEdits = ref([]);

const purchaseSummary = computed(
  () =>
    offers.value.find(o => o.id === Number(buy.value?.offer_id))?.rate ||
    doc.value?.rates.find(r => r.id === buy.value?.rate_id)
);
async function request(path, method = 'get', data = undefined) {
  const response = await axios({
    url: `/api/v1/accounts/${accountId.value}/tg_automation/${path}`,
    method,
    data,
    signal: controller.signal,
    headers: method === 'get' ? {} : { 'Content-Type': 'application/json' },
  });
  return response.data;
}
function changed() {
  dirty.value = true;
  notice.value = '';
}
function remember() {
  if (!doc.value) return;
  edits.value.push(JSON.stringify(serialize(doc.value)));
  if (edits.value.length > 30) edits.value.shift();
  redoEdits.value = [];
}
function undo() {
  if (!edits.value.length) return;
  redoEdits.value.push(JSON.stringify(serialize(doc.value)));
  doc.value = JSON.parse(edits.value.pop());
  changed();
}
function redo() {
  if (!redoEdits.value.length) return;
  edits.value.push(JSON.stringify(serialize(doc.value)));
  doc.value = JSON.parse(redoEdits.value.pop());
  changed();
}
async function copyGroupSettings() {
  const current = generation;
  try {
    const data = await request(`${copyGroup.value}/settings`);
    if (current !== generation) return;
    remember();
    doc.value = cloneSettings(data.document, doc.value.inbox_id);
    flowId.value = doc.value.flows[0]?.id || '';
    selectedId.value = '';
    changed();
    copyDialog.value.close();
    notice.value =
      'Настройки скопированы. Все сценарии выключены. Проверьте тарифы и сохраните.';
  } catch (e) {
    error.value = errorText(e);
  }
}
async function loadHistory(more = false) {
  const current = generation;
  const group = groupId.value;
  try {
    const [h, p] = await Promise.all([
      request(
        `${groupId.value}/history${more && historyNext.value ? `?before=${historyNext.value}` : ''}`
      ),
      request(
        `${groupId.value}/purchases${more && purchaseNext.value ? `?before=${purchaseNext.value}` : ''}`
      ),
    ]);
    if (current !== generation || group !== groupId.value) return;
    history.value = more
      ? [
          ...new Map(
            [...history.value, ...h.items].map(x => [x.id, x])
          ).values(),
        ]
      : h.items;
    purchases.value = more
      ? [
          ...new Map(
            [...purchases.value, ...p.items].map(x => [x.id, x])
          ).values(),
        ]
      : p.items;
    executions.value = h.executions || [];
    historyNext.value = h.next;
    purchaseNext.value = p.next;
  } catch (e) {
    error.value = errorText(e);
  }
}
async function load() {
  generation += 1;
  const current = generation;
  loading.value = true;
  error.value = '';
  uncertain.value = false;
  doc.value = null;
  history.value = [];
  executions.value = [];
  purchases.value = [];
  historyNext.value = null;
  purchaseNext.value = null;
  try {
    const data = await request(`${groupId.value}/settings`);
    if (current !== generation) return;
    doc.value = data.document;
    doc.value.message_limit ??= 3;
    lastTick.value = data.last_tick_at;
    revision.value = data.lock_version;
    dirty.value = false;
    edits.value = [];
    redoEdits.value = [];
    flowId.value = doc.value.flows.some(f => f.id === flowId.value)
      ? flowId.value
      : doc.value.flows[0]?.id || '';
    selectedId.value = flow.value?.nodes[0]?.id || '';
    await nextTick();
    fitView({ padding: 0.15 });
    if (section.value === 'history') await loadHistory();
  } catch (e) {
    if (current === generation) {
      error.value = errorText(e);
      denied.value = e.response?.status === 403;
    }
  } finally {
    if (current === generation) loading.value = false;
  }
}
async function boot() {
  controller.abort();
  controller = new AbortController();
  generation += 1;
  const current = generation;
  const targetAccount = accountId.value;
  loading.value = true;
  doc.value = null;
  history.value = [];
  executions.value = [];
  purchases.value = [];
  groups.value = [];
  sources.value = [];
  groupId.value = '';
  denied.value = false;
  buy.value = null;
  buyPending.value = null;
  buyUncertain.value = false;
  try {
    const data = await request('groups');
    if (current !== generation || targetAccount !== accountId.value) return;
    groups.value = data.groups;
    sources.value = data.sources;
    groupId.value = String(groups.value[0]?.id || '');
    if (groupId.value) await load();
    else {
      loading.value = false;
      error.value = 'Сначала подключите Telegram-группу.';
    }
  } catch (e) {
    if (targetAccount !== accountId.value) return;
    loading.value = false;
    denied.value = e.response?.status === 403;
    error.value = errorText(e);
  }
}
async function changeGroup(event) {
  const next = event.target.value;
  if (section.value === 'ideas' && !(await ideaEditor.value?.canLeave())) {
    event.target.value = groupId.value;
    return;
  }
  if (buyUncertain.value) {
    event.target.value = groupId.value;
    error.value = 'Сначала проверьте результат покупки в текущей группе.';
    return;
  }
  if (
    (dirty.value || buyUncertain.value) &&
    !window.confirm(
      'Есть несохранённые изменения или покупка без подтверждения результата. Переключить группу?'
    )
  ) {
    event.target.value = groupId.value;
    return;
  }
  groupId.value = next;
  await load();
}
async function save() {
  if (saving.value) return;
  saving.value = true;
  error.value = '';
  const current = generation;
  try {
    const result = await request(`${groupId.value}/settings`, 'put', {
      lock_version: revision.value,
      document: serialize(doc.value),
    });
    if (current !== generation) return;
    doc.value = result.document;
    revision.value = result.lock_version;
    dirty.value = false;
    uncertain.value = false;
    notice.value = 'Настройки сохранены.';
  } catch (e) {
    if (current === generation) {
      error.value = errorText(e);
      uncertain.value = !e.response || e.response.status >= 500;
    }
  } finally {
    if (current === generation) saving.value = false;
  }
}
function addFlow() {
  if (!doc.value.rates.length) {
    error.value = 'Сначала добавьте тариф.';
    section.value = 'rates';
    return;
  }
  remember();
  const f = newFlow(doc.value.rates[0].id);
  doc.value.flows.push(f);
  flowId.value = f.id;
  selectedId.value = f.nodes[0].id;
  changed();
}
function removeFlow() {
  if (
    !flow.value ||
    !window.confirm(
      'Удалить сценарий из настроек? История отправок сохранится.'
    )
  )
    return;
  remember();
  doc.value.flows = doc.value.flows.filter(f => f.id !== flowId.value);
  flowId.value = doc.value.flows[0]?.id || '';
  changed();
}
function addNode() {
  if (!flow.value) return;
  remember();
  const n = node(
    newType.value,
    140 + (flow.value.nodes.length % 2) * 300,
    flow.value.nodes.length * 75
  );
  flow.value.nodes.push(n);
  selectedId.value = n.id;
  changed();
}
function removeNode() {
  if (!selected.value) return;
  remember();
  flow.value.nodes = flow.value.nodes.filter(n => n.id !== selectedId.value);
  flow.value.edges = flow.value.edges.filter(
    e => e.source !== selectedId.value && e.target !== selectedId.value
  );
  selectedId.value = '';
  changed();
}
function connect(c) {
  if (!canConnect(flow.value, c)) {
    error.value =
      'Соединение создаёт петлю или выход уже занят. Удалите прежнюю связь либо выберите другой выход.';
    return;
  }
  remember();
  flow.value.edges.push(edge(c.source, c.target, c.sourceHandle || 'yes'));
  changed();
}
function deleteEdge() {
  if (!selectedEdge.value) return;
  remember();
  flow.value.edges = flow.value.edges.filter(e => e.id !== selectedEdge.value);
  selectedEdge.value = '';
  changed();
}
function editRate(rate) {
  rateEdit.value = rate
    ? { ...rate, rubles: String(rate.amount / 100) }
    : {
        id: uid(),
        name: '',
        rubles: '',
        amount: 0,
        count: 2,
        days: 14,
        mode: 'self',
        enabled: false,
      };
  rateDialog.value.open();
}
function applyRate() {
  const r = rateEdit.value;
  if (!/^\d{1,10}([.,]\d{1,2})?$/.test(String(r.rubles).trim())) {
    error.value = 'Укажите цену с точностью до копеек.';
    return;
  }
  const amount = Math.round(Number(String(r.rubles).replace(',', '.')) * 100);
  if (
    !r.name.trim() ||
    !Number.isSafeInteger(amount) ||
    amount < 0 ||
    !Number.isInteger(Number(r.count)) ||
    Number(r.count) < 1 ||
    !Number.isInteger(Number(r.days)) ||
    Number(r.days) < 1 ||
    Number(r.days) > 365
  ) {
    error.value = 'Проверьте название, цену, количество и срок тарифа.';
    return;
  }
  remember();
  const value = {
    id: r.id,
    name: r.name.trim(),
    amount,
    count: Number(r.count),
    days: Number(r.days),
    mode: r.mode,
    enabled: r.enabled,
  };
  const index = doc.value.rates.findIndex(v => v.id === value.id);
  if (index < 0) doc.value.rates.push(value);
  else doc.value.rates[index] = value;
  rateDialog.value.close();
  changed();
}
function startPurchase(rate) {
  if (dirty.value || uncertain.value) {
    error.value =
      'Сначала сохраните настройки и проверьте результат сохранения.';
    return;
  }
  if (!buyUncertain.value) {
    buy.value = {
      rate_id: rate.id,
      telegram_user_id: '',
      date_from: new Intl.DateTimeFormat('en-CA', {
        timeZone: doc.value.timezone,
      }).format(new Date()),
      payment_confirmed: false,
      offer_id: '',
    };
    buyKey.value = uid();
    buyPending.value = null;
    offers.value = [];
  }
  buyDialog.value.open();
}
async function findOffers() {
  try {
    offers.value = (
      await request(
        `${groupId.value}/offers?telegram_user_id=${encodeURIComponent(buy.value.telegram_user_id)}`
      )
    ).items;
  } catch (e) {
    error.value = errorText(e);
  }
}
async function lookupPurchase() {
  try {
    const result = await request(
      `${groupId.value}/purchases/lookup?request_key=${encodeURIComponent(buyKey.value)}`
    );
    if (result.found) {
      buyUncertain.value = false;
      buyPending.value = null;
      buyDialog.value.close();
      notice.value = result.calendar_client_id
        ? `Покупка подтверждена. Карточка календаря №${result.calendar_client_id}.`
        : `Покупка подтверждена. Размещение №${result.grant_id}.`;
      section.value = 'history';
      await loadHistory();
    } else
      error.value =
        'Покупка пока не найдена. Повторите ту же операцию с сохранённым ключом — дубль не создастся.';
  } catch (e) {
    error.value = errorText(e);
  }
}
async function confirmPurchase() {
  if (buyBusy.value) return;
  buyBusy.value = true;
  error.value = '';
  try {
    if (!buyPending.value)
      buyPending.value = {
        ...buy.value,
        telegram_user_id: Number(buy.value.telegram_user_id),
        offer_id: buy.value.offer_id ? Number(buy.value.offer_id) : null,
        request_key: buyKey.value,
      };
    const result = await request(
      `${groupId.value}/purchases`,
      'post',
      buyPending.value
    );
    buyUncertain.value = false;
    buyDialog.value.close();
    notice.value = result.calendar_client_id
      ? `Пакет создан. Для публикаций выберите в календаре карточку клиента №${result.calendar_client_id}.`
      : `Пакет выдан. Номер размещения: ${result.grant_id}.`;
    section.value = 'history';
    await loadHistory();
  } catch (e) {
    error.value = errorText(e);
    buyUncertain.value = !e.response || e.response.status >= 500;
    if (!buyUncertain.value) buyPending.value = null;
  } finally {
    buyBusy.value = false;
  }
}
async function pause(p) {
  try {
    await request(`${groupId.value}/purchases/${p.id}`, 'patch', {
      paused: !p.paused,
    });
    await loadHistory();
  } catch (e) {
    error.value = errorText(e);
  }
}
function stepSimulation(port = 'yes') {
  const n = flow.value?.nodes.find(v => v.id === simNode.value);
  if (!n || simulation.value.length >= 60) {
    simulationDone.value = true;
    return;
  }
  simulation.value.push({
    id: uid(),
    title: title(n),
    text:
      n.type === 'message'
        ? preview(
            n.data.text,
            doc.value,
            flow.value,
            groups.value.find(g => String(g.id) === groupId.value)?.title
          )
        : '',
  });
  if (['next', 'stop'].includes(n.type)) {
    simulationDone.value = true;
    return;
  }
  const e = flow.value.edges.find(
    v => v.source === n.id && (v.sourceHandle || 'yes') === port
  );
  simNode.value = e?.target || '';
  if (!simNode.value) simulationDone.value = true;
}
function startSimulation() {
  simulation.value = [];
  simNode.value = flow.value.nodes.find(n => n.type === 'event')?.id || '';
  simulationDone.value = false;
  simulateDialog.value.open();
  stepSimulation();
}
function beforeUnload(e) {
  if (dirty.value || uncertain.value || buyUncertain.value) {
    e.preventDefault();
    e.returnValue = '';
  }
}
onBeforeRouteLeave(async () => {
  if (section.value === 'ideas' && !(await ideaEditor.value?.canLeave()))
    return false;
  if (saving.value || buyBusy.value) return false;
  if (dirty.value || uncertain.value || buyUncertain.value)
    return window.confirm(
      'Есть несохранённые изменения или операция без подтверждения. Выйти из редактора?'
    );
  return true;
});
onBeforeRouteUpdate(async to => {
  if (String(to.params.accountId) !== String(accountId.value)) {
    if (saving.value || buyBusy.value || buyUncertain.value) return false;
    if (section.value === 'ideas' && !(await ideaEditor.value?.canLeave()))
      return false;
    if (dirty.value || uncertain.value)
      return window.confirm(
        'Настройки не сохранены. Переключить рабочую область?'
      );
  }
  return true;
});
async function changeSection(tab) {
  if (saving.value || buyBusy.value) return;
  if (section.value === 'ideas' && !(await ideaEditor.value?.canLeave()))
    return;
  error.value = '';
  notice.value = '';
  section.value = tab.key;
}
watch(section, v => {
  if (v === 'history' && groupId.value) loadHistory();
});
watch(accountId, boot);
onMounted(() => {
  listMode.value = window.matchMedia('(max-width: 768px)').matches;
  window.addEventListener('beforeunload', beforeUnload);
  boot();
});
onBeforeUnmount(() => {
  controller.abort();
  generation += 1;
  window.removeEventListener('beforeunload', beforeUnload);
});
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    loading-message="Загрузка автоматизации…"
  >
    <template #header>
      <BaseSettingsHeader
        title="Конструктор ботов"
        description="Тарифы, события и автоматические сообщения для каждой группы"
      >
        <template #actions>
          <Button
            label="К размещениям"
            slate
            outline
            @click="router.push({ name: 'telegram_placements' })"
          />
        </template>
      </BaseSettingsHeader>
      <div class="flex flex-wrap items-end justify-between gap-4">
        <label class="flex flex-col gap-2 text-sm text-n-slate-11"
          >Группа<select
            :value="groupId"
            :class="selectClass"
            :disabled="saving || buyBusy || !!ideaEditor?.isBusy"
            @change="changeGroup"
          >
            <option v-for="g in groups" :key="g.id" :value="String(g.id)">
              {{ g.title }}
            </option>
          </select></label
        >
        <div
          v-if="section !== 'ideas'"
          class="flex flex-wrap items-center gap-2"
        >
          <span v-if="dirty" class="text-n-amber-11">Есть изменения</span>
          <Button
            label="Обновить"
            slate
            outline
            :disabled="loading || saving"
            @click="load"
          /><Button
            label="Сохранить настройки"
            :is-loading="saving"
            :disabled="loading || denied || !doc"
            @click="save"
          />
        </div>
      </div>
      <div class="max-w-full overflow-x-auto py-1">
        <TabBar
          :tabs="tabs"
          :initial-active-tab="tabs.findIndex(t => t.key === section)"
          @tab-changed="changeSection"
        />
      </div>
    </template>
    <template #body>
      <Banner v-if="error" color="ruby" role="alert" class="mb-4">
        {{ error }}
      </Banner>
      <Banner v-if="notice" color="teal" role="status" class="mb-4">
        {{ notice }}
      </Banner>
      <Banner v-if="uncertain || buyUncertain" color="amber" class="mb-4">
        Ответ сервера не подтверждён. Для покупки используйте повтор этой же
        операции; не создавайте другую покупку, пока результат не проверен.
      </Banner>
      <IdeaMaps
        v-if="section === 'ideas' && groupId && !denied"
        :key="`${accountId}-${groupId}`"
        ref="ideaEditor"
        :account-id="accountId"
        :group="groups.find(g => String(g.id) === groupId)"
      />
      <fieldset
        v-if="!denied && doc && section !== 'ideas'"
        :disabled="saving"
        class="m-0 flex min-w-0 flex-col gap-5 border-0 p-0"
        :class="saving ? 'pointer-events-none opacity-70' : ''"
        @input="changed"
      >
        <details class="rounded-lg border border-n-weak p-3">
          <summary class="cursor-pointer text-sm text-n-slate-11">
            Настройки отправки ·
            {{
              sources.find(s => s.id === doc.inbox_id)?.name ||
              'выберите отправителя'
            }}
          </summary>
          <div class="mt-4 flex flex-col gap-4">
            <div class="flex flex-wrap items-end gap-5">
              <label class="flex flex-col gap-2 text-sm text-n-slate-11"
                >Отправитель<select
                  v-model="doc.inbox_id"
                  :class="selectClass"
                  @change="changed"
                >
                  <option :value="null" disabled>Выберите подключение</option>
                  <option v-for="s in sources" :key="s.id" :value="s.id">
                    {{ s.name }}
                  </option>
                </select></label
              >
              <label class="flex flex-col gap-2 text-sm text-n-slate-11"
                >Часовой пояс<select
                  v-model="doc.timezone"
                  :class="selectClass"
                  @change="changed"
                >
                  <option>Europe/Moscow</option>
                  <option>Asia/Yekaterinburg</option>
                  <option>Asia/Novosibirsk</option>
                </select></label
              >
              <label class="flex items-center gap-2 text-sm"
                ><input
                  v-model="doc.quiet"
                  type="checkbox"
                  @change="changed"
                />Не отправлять с 21:00 до 09:00</label
              >
            </div>
            <p class="text-xs text-n-slate-11">
              Последний опрос обработчика:
              {{
                lastTick
                  ? new Date(lastTick).toLocaleString('ru-RU')
                  : 'ожидается'
              }}. Старые размещения автоматически не запускаются.
            </p>
            <label class="flex flex-col gap-2 text-sm text-n-slate-11"
              >Не более сообщений одному клиенту за сутки (1–10)<input
                v-model.number="doc.message_limit"
                type="number"
                min="1"
                max="10"
                :class="selectClass"
                @input="changed"
            /></label>
          </div>
        </details>
        <template v-if="section === 'rates'">
          <div class="flex justify-between gap-3">
            <h3 class="text-base font-medium">Тарифы группы</h3>
            <Button
              label="Копировать из группы"
              slate
              outline
              @click="copyDialog.open()"
            />
            <Button
              label="Добавить тариф"
              icon="i-lucide-plus"
              @click="editRate(null)"
            />
          </div>
          <p v-if="!doc.rates.length" class="py-10 text-center text-n-slate-11">
            Тарифов пока нет. Добавьте базовый, стандартный, VIP и нужные
            спецпакеты.
          </p>
          <div
            v-for="r in doc.rates"
            :key="r.id"
            class="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-n-weak p-4"
          >
            <div>
              <p class="font-medium text-n-slate-12">{{ r.name }}</p>
              <p class="text-sm text-n-slate-11">
                {{ r.amount / 100 }} ₽ · {{ r.count }} публикаций ·
                {{ r.days }} дн. ·
                {{
                  r.mode === 'admin'
                    ? 'Администратор через календарь'
                    : 'Публикует клиент'
                }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ r.enabled ? 'Доступен для новых покупок' : 'Выключен' }}
              </p>
            </div>
            <div class="flex gap-2">
              <Button
                label="Изменить"
                slate
                outline
                @click="editRate(r)"
              /><Button
                label="Выдать пакет"
                :disabled="!r.enabled || !r.amount || dirty || uncertain"
                @click="startPurchase(r)"
              />
            </div>
          </div>
          <p class="text-sm text-n-slate-11">
            Изменение тарифа не меняет условия уже купленных пакетов.
            Спецпредложение создавайте отдельным тарифом.
          </p>
        </template>
        <template v-if="section === 'flows'">
          <div class="flex flex-wrap items-end justify-between gap-3">
            <label class="flex flex-col gap-2 text-sm text-n-slate-11"
              >Сценарий<select
                v-model="flowId"
                :class="selectClass"
                @input.stop
                @change="selectedId = flow?.nodes[0]?.id || ''"
              >
                <option v-if="!doc.flows.length" value="">
                  Сценариев пока нет
                </option>
                <option v-for="f in doc.flows" :key="f.id" :value="f.id">
                  {{ f.name }}
                </option>
              </select></label
            >
            <div class="flex flex-wrap gap-2">
              <Button
                label="Новый сценарий"
                slate
                outline
                @click="addFlow"
              /><Button
                label="Дублировать"
                slate
                outline
                :disabled="!flow"
                @click="
                  remember();
                  doc.flows.push({
                    ...JSON.parse(JSON.stringify(flow)),
                    id: uid(),
                    name: flow.name + ' · копия',
                    enabled: false,
                  });
                  changed();
                "
              /><Button
                label="Удалить"
                ruby
                ghost
                :disabled="!flow"
                @click="removeFlow"
              />
            </div>
          </div>
          <template v-if="flow">
            <div class="flex flex-wrap items-end gap-4">
              <Input
                v-model="flow.name"
                label="Название сценария"
                class="min-w-0 flex-1"
                @focus="remember"
              />
              <label class="flex flex-col gap-2 text-sm text-n-slate-11"
                >Тариф покупки<select
                  v-model="flow.rate_id"
                  :class="selectClass"
                  @change="changed"
                >
                  <option v-for="r in doc.rates" :key="r.id" :value="r.id">
                    {{ r.name }}
                  </option>
                </select></label
              >
              <label class="flex items-center gap-2 py-2 text-sm"
                ><input
                  v-model="flow.enabled"
                  type="checkbox"
                  @change="changed"
                />Включён для новых пакетов</label
              >
            </div>
            <div class="flex flex-wrap gap-2">
              <select
                v-model="newType"
                :class="selectClass"
                aria-label="Тип нового блока"
                @input.stop
              >
                <option v-for="t in types" :key="t.value" :value="t.value">
                  {{ t.label }}
                </option></select
              ><Button
                label="Добавить блок"
                slate
                outline
                @click="addNode"
              /><Button
                label="Отменить"
                slate
                ghost
                :disabled="!edits.length"
                @click="undo"
              /><Button
                label="Вернуть"
                slate
                ghost
                :disabled="!redoEdits.length"
                @click="redo"
              /><Button
                :label="listMode ? 'Схема' : 'Список'"
                slate
                outline
                @click="listMode = !listMode"
              /><Button
                label="Проверить без отправки"
                slate
                outline
                @click="startSimulation"
              />
            </div>
            <div
              class="automation-editor grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_300px]"
            >
              <div class="min-w-0 rounded-lg border border-n-weak bg-n-solid-2">
                <div class="flex flex-wrap gap-2 border-b border-n-weak p-2">
                  <Button
                    label="Уместить"
                    slate
                    ghost
                    @click="fitView({ padding: 0.15 })"
                  /><Button label="＋" slate ghost @click="zoomIn" /><Button
                    label="−"
                    slate
                    ghost
                    @click="zoomOut"
                  /><Button
                    label="Удалить связь"
                    slate
                    ghost
                    :disabled="!selectedEdge"
                    @click="deleteEdge"
                  />
                </div>
                <div v-if="listMode" class="flex flex-col gap-3 p-4">
                  <button
                    v-for="n in flow.nodes"
                    :key="n.id"
                    type="button"
                    class="rounded-lg border bg-n-background p-4 text-left"
                    :class="
                      n.id === selectedId ? 'border-n-brand' : 'border-n-weak'
                    "
                    @click="selectedId = n.id"
                  >
                    <span class="text-xs text-n-slate-11">{{
                      types.find(t => t.value === n.type)?.label
                    }}</span>
                    <p class="mt-1 break-words text-sm font-medium">
                      {{ title(n) }}
                    </p>
                  </button>
                </div>
                <FlowCanvas
                  v-else
                  :key="canvasKey"
                  ref="canvas"
                  :flow="flow"
                  :node-types="nodeTypes"
                  class="automation-canvas"
                  @connect="connect"
                  @node-click="
                    selectedId = $event.node.id;
                    selectedEdge = '';
                  "
                  @edge-click="selectedEdge = $event.edge.id"
                  @node-drag-start="remember"
                  @node-drag-stop="changed"
                />
              </div>
              <div class="min-w-0 rounded-lg border border-n-weak p-4">
                <template v-if="selected">
                  <div class="mb-4 flex justify-between gap-2">
                    <h3 class="text-sm font-medium">
                      {{ types.find(t => t.value === selected.type)?.label }}
                    </h3>
                    <Button
                      label="Удалить блок"
                      ruby
                      ghost
                      size="xs"
                      @click="removeNode"
                    />
                  </div>
                  <div class="flex flex-col gap-4">
                    <Input
                      v-model="selected.data.label"
                      label="Название блока"
                      @focus="remember"
                    />
                    <label
                      v-if="selected.type === 'event'"
                      class="flex flex-col gap-2 text-sm"
                      >Событие<select
                        v-model="selected.data.event"
                        :class="selectClass"
                        @change="changed"
                      >
                        <option
                          v-for="(name, key) in eventLabels"
                          :key="key"
                          :value="key"
                        >
                          {{ name }}
                        </option>
                      </select></label
                    >
                    <label
                      v-if="selected.type === 'condition'"
                      class="flex flex-col gap-2 text-sm"
                      >Проверка<select
                        v-model="selected.data.condition"
                        :class="selectClass"
                        @change="changed"
                      >
                        <option
                          v-for="(name, key) in conditionLabels"
                          :key="key"
                          :value="key"
                        >
                          {{ name }}
                        </option>
                      </select></label
                    >
                    <Input
                      v-if="selected.type === 'delay'"
                      v-model.number="selected.data.minutes"
                      label="Ожидание, минут"
                      type="number"
                      min="0"
                      max="43200"
                    />
                    <template v-if="selected.type === 'message'">
                      <TextArea
                        v-model="selected.data.text"
                        label="Текст сообщения"
                        :rows="8"
                        maxlength="3000"
                        @focus="remember"
                      />
                      <div class="flex flex-wrap gap-1">
                        <Button
                          v-for="token in tokens"
                          :key="token"
                          :label="'{' + token + '}'"
                          slate
                          ghost
                          size="xs"
                          @click="
                            selected.data.text += '{' + token + '}';
                            changed();
                          "
                        />
                      </div>
                      <Button
                        label="Предпросмотр"
                        slate
                        outline
                        @click="previewDialog.open()"
                      />
                    </template>
                    <template v-if="selected.type === 'offer'">
                      <label class="flex flex-col gap-2 text-sm"
                        >Предлагаемый тариф<select
                          v-model="selected.data.rate_id"
                          :class="selectClass"
                          @change="changed"
                        >
                          <option value="">Выберите тариф</option>
                          <option
                            v-for="r in doc.rates"
                            :key="r.id"
                            :value="r.id"
                          >
                            {{ r.name }} · {{ r.amount / 100 }} ₽
                          </option>
                        </select></label
                      ><Input
                        v-model.number="selected.data.hours"
                        type="number"
                        label="Предложение действует, часов"
                        min="1"
                        max="720"
                      />
                    </template>
                    <label
                      v-if="selected.type === 'next'"
                      class="flex flex-col gap-2 text-sm"
                      >Сценарий после использования нового пакета<select
                        v-model="selected.data.flow_id"
                        :class="selectClass"
                        @change="changed"
                      >
                        <option value="">Выберите сценарий</option>
                        <option
                          v-for="f in doc.flows"
                          :key="f.id"
                          :value="f.id"
                        >
                          {{ f.name }}
                        </option>
                      </select></label
                    >
                    <p
                      v-if="selected.type === 'purchase'"
                      class="text-sm text-n-slate-11"
                    >
                      Продолжение произойдёт после подтверждения оплаты и выдачи
                      пакета с выбором этого предложения.
                    </p>
                    <template v-if="!['stop', 'next'].includes(selected.type)">
                      <label
                        v-for="port in selected.type === 'condition'
                          ? ['yes', 'no']
                          : ['yes']"
                        :key="port"
                        class="flex flex-col gap-2 text-sm"
                        >{{
                          selected.type === 'condition'
                            ? port === 'yes'
                              ? 'Если да'
                              : 'Если нет'
                            : 'Следующий блок'
                        }}<select
                          :value="
                            flow.edges.find(
                              e =>
                                e.source === selected.id &&
                                (e.sourceHandle || 'yes') === port
                            )?.target || ''
                          "
                          :class="selectClass"
                          @change="
                            remember();
                            flow.edges = flow.edges.filter(
                              e =>
                                !(
                                  e.source === selected.id &&
                                  (e.sourceHandle || 'yes') === port
                                )
                            );
                            $event.target.value &&
                              connect({
                                source: selected.id,
                                target: $event.target.value,
                                sourceHandle: port,
                              });
                            changed();
                          "
                        >
                          <option value="">Не соединён</option>
                          <option
                            v-for="n in flow.nodes.filter(
                              n => n.id !== selected.id && n.type !== 'event'
                            )"
                            :key="n.id"
                            :value="n.id"
                          >
                            {{ title(n) }}
                          </option>
                        </select></label
                      >
                    </template>
                    <p class="text-xs text-n-slate-11">
                      Перед отправкой система повторно проверит пакет, ответы
                      клиента и ручную паузу.
                    </p>
                  </div>
                </template>
                <p v-else class="text-sm text-n-slate-11">
                  Выберите блок на схеме или в списке.
                </p>
              </div>
            </div>
          </template>
          <p v-else class="py-10 text-center text-n-slate-11">
            Создайте первый сценарий. Отправка начнётся только после сохранения
            включённого сценария.
          </p>
        </template>
        <template v-if="section === 'history'">
          <div class="flex justify-between gap-3">
            <h3 class="font-medium">Отправки</h3>
            <Button
              label="Обновить историю"
              slate
              outline
              @click="loadHistory()"
            />
          </div>
          <p v-if="!history.length" class="py-6 text-center text-n-slate-11">
            Автоматических отправок пока нет.
          </p>
          <div
            v-for="h in history"
            :key="h.id"
            class="flex flex-wrap justify-between gap-3 border-b border-n-weak py-3 text-sm"
          >
            <span
              >Пакет №{{ h.purchase_id }} ·
              {{ statusLabels[h.state] || 'Нужна проверка' }}</span
            ><Button
              v-if="h.conversation_display_id"
              label="Открыть диалог"
              slate
              ghost
              @click="
                router.push(
                  `/app/accounts/${accountId}/conversations/${h.conversation_display_id}`
                )
              "
            />
          </div>
          <h3 class="mt-5 font-medium">Этапы клиентов в сценариях</h3>
          <p v-if="!executions.length" class="text-sm text-n-slate-11">
            Сценарии ещё не запускались.
          </p>
          <div
            v-for="r in executions"
            :key="r.id"
            class="border-b border-n-weak py-3 text-sm"
          >
            <p class="font-medium">
              Покупка №{{ r.purchase_id }} · {{ r.flow }}
            </p>
            <p class="text-n-slate-11">
              {{
                {
                  running: 'Ожидание шага',
                  waiting: 'Ожидается покупка',
                  complete: 'Завершён',
                  cancelled: 'Остановлен',
                  attention: 'Нужна проверка',
                }[r.state] || 'Нужна проверка'
              }}
              · {{ r.node || 'Конец сценария' }}
            </p>
            <p v-if="r.state === 'running'" class="text-xs text-n-slate-11">
              Следующая проверка:
              {{ new Date(r.due_at).toLocaleString('ru-RU') }}
            </p>
          </div>
          <h3 class="mt-5 font-medium">Выданные пакеты и ручная пауза</h3>
          <p v-if="!purchases.length" class="py-6 text-center text-n-slate-11">
            Новых пакетов через автоматизацию пока не выдавали. Старые
            размещения здесь не запускаются.
          </p>
          <div
            v-for="p in purchases"
            :key="p.id"
            class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak py-3 text-sm"
          >
            <span
              >{{
                p.calendar_client_id
                  ? 'Календарь №' + p.calendar_client_id
                  : 'Размещение №' + p.grant_id
              }}
              · {{ p.tariff }} · {{ p.amount / 100 }} ₽ · Telegram ID
              {{ p.telegram_user_id }}</span
            ><Button
              :label="p.paused ? 'Снять паузу' : 'Не отправлять предложения'"
              slate
              outline
              @click="pause(p)"
            />
          </div>
          <Button
            v-if="historyNext || purchaseNext"
            label="Показать ещё"
            slate
            outline
            @click="loadHistory(true)"
          />
        </template>
      </fieldset>
    </template>
  </SettingsLayout>
  <Dialog
    ref="copyDialog"
    title="Копировать настройки группы"
    confirm-button-label="Скопировать выключенными"
    :disable-confirm-button="!copyGroup"
    @confirm="copyGroupSettings"
  >
    <div class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        Каталог и схемы текущей группы будут заменены копией. Все скопированные
        сценарии выключены, покупки и история сохраняются.
      </p>
      <label class="flex flex-col gap-2 text-sm"
        >Исходная группа<select v-model="copyGroup" :class="selectClass">
          <option value="">Выберите группу</option>
          <option
            v-for="g in groups.filter(g => String(g.id) !== groupId)"
            :key="g.id"
            :value="String(g.id)"
          >
            {{ g.title }}
          </option>
        </select></label
      ><Banner v-if="error" color="ruby">{{ error }}</Banner>
    </div>
  </Dialog>
  <Dialog
    ref="rateDialog"
    title="Тариф группы"
    show-cancel-button
    confirm-button-label="Применить"
    @confirm="applyRate"
  >
    <div v-if="rateEdit" class="flex flex-col gap-4">
      <Banner v-if="error" color="ruby">{{ error }}</Banner>
      <Input v-model="rateEdit.name" label="Название" maxlength="120" /><Input
        v-model="rateEdit.rubles"
        label="Стоимость, ₽"
      /><Input
        v-model.number="rateEdit.count"
        label="Публикаций"
        type="number"
        min="1"
      /><Input
        v-model.number="rateEdit.days"
        label="Срок, дней"
        type="number"
        min="1"
        max="365"
      /><label class="flex flex-col gap-2 text-sm"
        >Кто публикует<select v-model="rateEdit.mode" :class="selectClass">
          <option value="self">Клиент</option>
          <option value="admin">Администратор через календарь</option>
        </select></label
      ><label class="flex items-center gap-2 text-sm"
        ><input v-model="rateEdit.enabled" type="checkbox" />Доступен для новых
        покупок</label
      >
      <p class="text-sm text-n-slate-11">
        После применения сохраните настройки группы. Прежние покупки не
        изменятся.
      </p>
    </div>
  </Dialog>
  <Dialog
    ref="buyDialog"
    title="Подтвердить оплату и выдать пакет"
    :show-cancel-button="!buyBusy"
    :is-loading="buyBusy"
    confirm-button-label="Выдать оплаченный пакет"
    :disable-confirm-button="
      !buy?.payment_confirmed || !buy?.telegram_user_id || buyBusy
    "
    @confirm="confirmPurchase"
  >
    <div v-if="buy" class="flex flex-col gap-4">
      <p
        v-if="purchaseSummary?.mode === 'admin'"
        class="text-sm text-n-slate-11"
      >
        Будет создана отдельная карточка календаря для этого пакета. Счётчик
        учитывает подтверждённые публикации из этой карточки. Самостоятельный
        доступ клиенту не выдаётся.
      </p>
      <Button
        v-if="buyUncertain"
        label="Проверить результат покупки"
        slate
        outline
        @click="lookupPurchase"
      />
      <p v-if="purchaseSummary" class="text-sm font-medium">
        {{ purchaseSummary.name }} · {{ purchaseSummary.amount / 100 }} ₽ ·
        {{ purchaseSummary.count }} публикаций · {{ purchaseSummary.days }} дн.
      </p>
      <Banner v-if="error" color="ruby">{{ error }}</Banner
      ><Input
        v-model="buy.telegram_user_id"
        label="Telegram User ID клиента"
        :disabled="buyUncertain || buyBusy"
      /><Button
        label="Проверить спецпредложения"
        slate
        outline
        :disabled="!buy.telegram_user_id || buyUncertain"
        @click="findOffers"
      /><label class="flex flex-col gap-2 text-sm"
        >Предложение<select
          v-model="buy.offer_id"
          :class="selectClass"
          :disabled="buyUncertain || buyBusy"
          @change="
            buy.rate_id =
              offers.find(o => o.id === Number(buy.offer_id))?.rate.id ||
              buy.rate_id
          "
        >
          <option value="">Обычная покупка по тарифу</option>
          <option v-for="o in offers" :key="o.id" :value="o.id">
            {{ o.rate.name }} · {{ o.rate.count }} за
            {{ o.rate.amount / 100 }} ₽
          </option>
        </select></label
      ><Input
        v-model="buy.date_from"
        type="date"
        label="Начало действия"
        :disabled="buyUncertain || buyBusy"
      /><label class="flex items-center gap-2 text-sm"
        ><input
          v-model="buy.payment_confirmed"
          type="checkbox"
          :disabled="buyUncertain || buyBusy"
        />Оплату проверил, деньги поступили</label
      >
      <p class="text-sm text-n-slate-11">
        Доступ будет выдан реальному клиенту. Автоматическая банковская проверка
        не выполняется.
      </p>
    </div>
  </Dialog>
  <Dialog ref="previewDialog" title="Предпросмотр сообщения">
    <p class="whitespace-pre-wrap break-words text-sm text-n-slate-12">
      {{ previewBody }}
    </p>
  </Dialog>
  <Dialog ref="simulateDialog" title="Проверка схемы без отправки">
    <div class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        Только просмотр выбранных переходов. Telegram, платежи и доступ не
        изменяются.
      </p>
      <div
        v-for="s in simulation"
        :key="s.id"
        class="border-b border-n-weak pb-3 text-sm"
      >
        <p class="font-medium">{{ s.title }}</p>
        <p v-if="s.text" class="mt-2 whitespace-pre-wrap">{{ s.text }}</p>
      </div>
      <div v-if="!simulationDone" class="flex gap-2">
        <Button
          label="Следующий шаг / Да"
          @click="stepSimulation('yes')"
        /><Button
          v-if="flow?.nodes.find(n => n.id === simNode)?.type === 'condition'"
          label="Нет"
          slate
          outline
          @click="stepSimulation('no')"
        />
      </div>
      <p v-else class="text-sm text-n-slate-11">
        Проверка выбранного пути завершена.
      </p>
    </div>
  </Dialog>
</template>

<style scoped>
.automation-canvas {
  height: 600px;
}
</style>
